<script setup lang="ts">
import { ref, computed } from 'vue';
import Tooltip from 'primevue/tooltip';
import BaseIcon from '@/components/base/BaseIcon.vue';
import JsonViewer from '@/components/base/JsonViewer.vue';
import KycPepTitularV1 from '@/components/company-kyc/data-renderers/KycPepTitularV1.vue';
import KycPepRelatedV1 from '@/components/company-kyc/data-renderers/KycPepRelatedV1.vue';
import KycEconomicActivityV1 from '@/components/company-kyc/data-renderers/KycEconomicActivityV1.vue';
import KycAgePersonV1 from '@/components/company-kyc/data-renderers/KycAgePersonV1.vue';
import KycLocationCountryV1 from '@/components/company-kyc/data-renderers/KycLocationCountryV1.vue';
import KycLocationCityV1 from '@/components/company-kyc/data-renderers/KycLocationCityV1.vue';
import KycNegativeMediaV1 from '@/components/company-kyc/data-renderers/KycNegativeMediaV1.vue';
import KycLawsuitsV1 from '@/components/company-kyc/data-renderers/KycLawsuitsV1.vue';
import KycLawsuitsNovoV1 from '@/components/company-kyc/data-renderers/KycLawsuitsNovoV1.vue';
import KycNationalSanctionsV1 from '@/components/company-kyc/data-renderers/KycNationalSanctionsV1.vue';
import { formatTimeAgo } from '@/utils/time';
import {
  getCategoryIcon,
  getRiskCategoryLabel,
  getRiskLevelDisplay
} from '@/utils/kyc';
import { formatDate } from '@/utils/formatters';
import type { KycEntity, KycCategory } from '@/types/kyc';

defineOptions({
  directives: { tooltip: Tooltip }
});

interface Props {
  entity: KycEntity;
  categoryOrder?: string[];
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  categoryOrder: () => [],
  loading: false
});

// Track currently expanded category (null = none expanded)
const expandedCategory = ref<string | null>(null);

const toggleCategory = (slug: string) => {
  expandedCategory.value = expandedCategory.value === slug ? null : slug;
};

const isCategoryExpanded = (slug: string) => expandedCategory.value === slug;

// Sort categories by categoryOrder, then by risk level (highest first)
const sortedCategories = computed<KycCategory[]>(() => {
  if (!props.entity?.categories) return [];
  
  const categories = props.entity.categories.filter(c => c.is_relevant !== false);
  
  return [...categories].sort((a, b) => {
    // First sort by category order if provided
    if (props.categoryOrder.length > 0) {
      const indexA = props.categoryOrder.indexOf(a.category_slug);
      const indexB = props.categoryOrder.indexOf(b.category_slug);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
    }
    // Then by risk level (highest first)
    const riskA = a.level_value ?? 0;
    const riskB = b.level_value ?? 0;
    if (riskB !== riskA) return riskB - riskA;
    // Finally alphabetically
    return getRiskCategoryLabel(a.category_slug, a.category_display_name)
      .localeCompare(getRiskCategoryLabel(b.category_slug, b.category_display_name), 'pt-BR');
  });
});

const getCategoryName = (category: KycCategory): string => {
  return getRiskCategoryLabel(category.category_slug, category.category_display_name);
};

const getCategoryRiskDisplay = (levelValue: number | null | undefined) => getRiskLevelDisplay(levelValue);

// Get the currently expanded category object
const expandedCategoryData = computed<KycCategory | null>(() => {
  if (!expandedCategory.value) return null;
  return sortedCategories.value.find(c => c.category_slug === expandedCategory.value) || null;
});

const expandedCategoryRiskDisplay = computed(() => {
  if (!expandedCategoryData.value) return null;
  return getRiskLevelDisplay(expandedCategoryData.value.level_value);
});
</script>

<template>
  <div class="space-y-5">
    <!-- Loading skeleton -->
    <template v-if="loading">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <div v-for="n in 8" :key="n" class="animate-pulse">
          <div class="h-32 bg-gray-100 rounded-xl"></div>
        </div>
      </div>
    </template>

    <!-- Checks grid -->
    <template v-else>
      <!-- Grid of check cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <button
          v-for="category in sortedCategories"
          :key="category.category_slug"
          @click="toggleCategory(category.category_slug)"
          :class="[
            'group relative text-left rounded-xl transition-all duration-200 overflow-hidden',
            'border-l-4 shadow-sm hover:shadow-lg cursor-pointer',
            getCategoryRiskDisplay(category.level_value).borderClass,
            isCategoryExpanded(category.category_slug)
              ? ['ring-2 ring-offset-1 shadow-lg', getCategoryRiskDisplay(category.level_value).colorClass.replace('text-', 'ring-')]
              : ''
          ]"
        >
          <!-- Card background with subtle gradient -->
          <div 
            :class="[
              'absolute inset-0 opacity-50',
              getCategoryRiskDisplay(category.level_value).bgClass
            ]"
          ></div>
          <div class="absolute inset-0 bg-gradient-to-br from-white via-white to-transparent"></div>
          
          <!-- Card content -->
          <div class="relative p-4">
            <!-- Top row: Icon + Name + Risk Badge -->
            <div class="flex items-start justify-between gap-3 mb-3">
              <div class="flex items-center gap-3 min-w-0">
                <div :class="[
                  'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
                  getCategoryRiskDisplay(category.level_value).bgClass
                ]">
                  <BaseIcon :class="['text-xl', getCategoryRiskDisplay(category.level_value).colorClass]">
                    {{ getCategoryIcon(category.category_slug) }}
                  </BaseIcon>
                </div>
                <h3 class="text-base font-semibold text-gray-900 leading-tight">
                  {{ getCategoryName(category) }}
                </h3>
              </div>
              
              <!-- Outdated indicator -->
              <div
                v-if="category.is_outdated"
                class="flex-shrink-0 flex items-center gap-1 text-orange-500"
                title="Desatualizado"
              >
                <BaseIcon class="text-lg">schedule</BaseIcon>
              </div>
            </div>

            <!-- Risk level badge -->
            <div class="flex items-center justify-between">
              <span 
                :class="[
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold border',
                  getCategoryRiskDisplay(category.level_value).bgClass,
                  getCategoryRiskDisplay(category.level_value).colorClass,
                  getCategoryRiskDisplay(category.level_value).borderClass
                ]"
              >
                <BaseIcon class="text-xl">{{ getCategoryRiskDisplay(category.level_value).icon }}</BaseIcon>
                {{ getCategoryRiskDisplay(category.level_value).label }}
                <span v-if="(category.level_value ?? 0) > 0"></span>
              </span>
              
              <!-- Expand indicator -->
              <BaseIcon 
                :class="[
                  'text-lg transition-transform duration-200',
                  isCategoryExpanded(category.category_slug) ? 'rotate-180 text-gray-700' : 'text-gray-400 group-hover:text-gray-600'
                ]"
              >
                expand_more
              </BaseIcon>
            </div>

            <!-- Last checked date -->
            <div class="mt-3 pt-3 border-t border-gray-200/60 text-xs text-gray-500">
              <span v-if="category.last_checked_at">
                Verificado {{ formatTimeAgo(category.last_checked_at) }}
              </span>
              <span v-else>Ainda não verificado</span>
            </div>
          </div>
        </button>
      </div>

      <!-- Expanded details panel (shown below when a category is selected) -->
      <transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div
          v-if="expandedCategoryData && expandedCategoryRiskDisplay"
          :class="[
            'rounded-xl overflow-hidden shadow-xl border-l-4',
            expandedCategoryRiskDisplay.borderClass
          ]"
        >
          <!-- Header with gradient background -->
          <div
            :class="[
              'relative flex items-center justify-between px-6 py-5',
              expandedCategoryRiskDisplay.bgClass
            ]"
          >
            <!-- Background pattern -->
            <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
            
            <div class="relative flex items-center gap-4">
              <div :class="[
                'w-12 h-12 rounded-xl flex items-center justify-center shadow-sm',
                'bg-white/80 backdrop-blur-sm'
              ]">
                <BaseIcon :class="['text-2xl', expandedCategoryRiskDisplay.colorClass]">
                  {{ getCategoryIcon(expandedCategoryData.category_slug) }}
                </BaseIcon>
              </div>
              <div>
                <h3 class="font-bold text-gray-900 text-xl">
                  {{ getCategoryName(expandedCategoryData) }}
                </h3>
                <p class="text-sm text-gray-600 mt-0.5">
                  <span v-if="expandedCategoryData.last_checked_at">
                    Última verificação: {{ formatDate(expandedCategoryData.last_checked_at, undefined, 'N/A', 'en-US') }}
                    <span class="text-gray-400">({{ formatTimeAgo(expandedCategoryData.last_checked_at) }})</span>
                  </span>
                  <span v-else>Última verificação: Desconhecida</span>
                </p>
              </div>
            </div>
            
            <div class="relative flex items-center gap-4">
              <!-- Risk level badge -->
              <span 
                :class="[
                  'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border-2 shadow-sm',
                  'bg-white/90 backdrop-blur-sm',
                  expandedCategoryRiskDisplay.colorClass,
                  expandedCategoryRiskDisplay.borderClass
                ]"
              >
                <BaseIcon class="text-xl">{{ expandedCategoryRiskDisplay.icon }}</BaseIcon>
                {{ expandedCategoryRiskDisplay.label }}
                <span v-if="(expandedCategoryData.level_value ?? 0) > 0">Risco</span>
              </span>
              
              <!-- Close button -->
              <button
                @click="expandedCategory = null"
                class="p-2 rounded-xl bg-white/80 hover:bg-white shadow-sm transition-colors"
              >
                <BaseIcon class="text-xl text-gray-600">close</BaseIcon>
              </button>
            </div>
          </div>

          <!-- Content -->
          <div class="px-6 py-5 bg-white border-t border-gray-100">
            <!-- Notes if present -->
            <div v-if="expandedCategoryData.notes" class="text-sm text-gray-600 mb-5 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div class="flex items-start gap-2">
                <BaseIcon class="text-amber-500 text-lg flex-shrink-0 mt-0.5">info</BaseIcon>
                <div>
                  <span class="font-semibold text-amber-700">Observações:</span>
                  <span class="text-amber-800 ml-1">{{ expandedCategoryData.notes }}</span>
                </div>
              </div>
            </div>

            <!-- Data content -->
            <div v-if="expandedCategoryData.data_json">
              <!-- Custom renderers for specific category types -->
              <template v-if="expandedCategoryData.category_slug === 'pep_titular' && (expandedCategoryData.data_version === '1' || expandedCategoryData.data_version === '1.01')">
                <KycPepTitularV1 :category="expandedCategoryData" />
              </template>
              <template v-else-if="expandedCategoryData.category_slug === 'pep_related' && (expandedCategoryData.data_version === '1' || expandedCategoryData.data_version === '1.01')">
                <KycPepRelatedV1 :category="expandedCategoryData" />
              </template>
              <template v-else-if="expandedCategoryData.category_slug === 'economic_activity' && expandedCategoryData.data_version === '1'">
                <KycEconomicActivityV1 :category="expandedCategoryData" />
              </template>
              <template v-else-if="expandedCategoryData.category_slug === 'age_person' && expandedCategoryData.data_version === '1'">
                <KycAgePersonV1 :category="expandedCategoryData" />
              </template>
              <template v-else-if="expandedCategoryData.category_slug === 'location_city' && expandedCategoryData.data_version === '1'">
                <KycLocationCityV1 :category="expandedCategoryData" />
              </template>
              <template v-else-if="expandedCategoryData.category_slug === 'location_country' && expandedCategoryData.data_version === '1'">
                <KycLocationCountryV1 :category="expandedCategoryData" />
              </template>
              <template v-else-if="['national_sanctions', 'sanctions_national'].includes(expandedCategoryData.category_slug) && expandedCategoryData.data_version === '1'">
                <KycNationalSanctionsV1 :category="expandedCategoryData" />
              </template>
              <template v-else-if="expandedCategoryData.category_slug === 'negative_media' && expandedCategoryData.data_version === '1'">
                <KycNegativeMediaV1 :category="expandedCategoryData" />
              </template>
              <template v-else-if="expandedCategoryData.category_slug === 'lawsuits'">
                <KycLawsuitsV1 :category="expandedCategoryData" />
              </template>
              <template v-else-if="expandedCategoryData.category_slug === 'lawsuits_novo'">
                <KycLawsuitsNovoV1 :category="expandedCategoryData" />
              </template>
              <!-- Fallback to generic JSON viewer -->
              <template v-else>
                <JsonViewer :json-data="expandedCategoryData.data_json" />
              </template>
            </div>
            <div v-else class="text-sm text-gray-400 py-8 text-center bg-gray-50 rounded-xl">
              <BaseIcon class="text-4xl text-gray-300 mb-2">folder_open</BaseIcon>
              <p>Não há dados adicionais disponíveis para esta verificação.</p>
            </div>
          </div>
        </div>
      </transition>

    </template>

    <!-- Empty state -->
    <div v-if="!loading && sortedCategories.length === 0" class="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
      <BaseIcon class="text-6xl text-gray-300 mb-4">fact_check</BaseIcon>
      <h3 class="text-lg font-semibold text-gray-600 mb-1">Nenhuma verificação de KYX encontrada</h3>
      <p class="text-gray-400">Não há verificações de conformidade disponíveis para esta pessoa.</p>
    </div>
  </div>
</template>
